import { IIntegratedCare, IReminder, ISingleOrder } from '@alberta/konexi-shared';
import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { AlertController, ModalController, NavParams } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { OrderDeliveryInformationService } from 'src/app/business/order/order-delivery-information-service';
import { getReminderForm } from 'src/app/business/reminder/reminder-form';
import { getSingleOrderForm } from 'src/app/business/single-order/single-order-formbuilder';
import { SingleOrderViewModel } from 'src/app/business/single-order/single-order-view-model';
import { map } from 'src/app/common/mapper/mapper';
import { IntegratedCareDB, ReminderDB } from 'src/app/common/repository/databases';
import { observe } from 'src/app/common/tracking';
import { WizardNavPageComponent } from 'src/app/shared/components/wizard-page/wizard-nav-page/wizard-nav-page.component';
import { ConfigDto } from 'src/app/shared/models/config/config-dto.model';
import { SingleOrderDto } from 'src/app/shared/models/single-order/single-order-dto.model';
import { WizardStepItem } from 'src/app/shared/models/wizard-step-item';
import { AuthService } from 'src/app/shared/services/auth.service';
import { QueryService } from 'src/app/shared/services/query/query.service';

import { InfoBoxType } from 'src/app/shared/components/common/info-box/info-box.component';
import { SingleOrderComponentModel } from './../../../../../../business/single-order/single-order-component-model';
import { TenantConfigService } from './../../../../../../shared/services/tenant-config/tenant-config.service';
import { ReminderEditService } from './reminder-edit.service';
import WIZARD_ITEMS from './single-order-edit-wizard-items';

@Component({
  selector: 'itl-single-order-edit',
  templateUrl: './single-order-edit.component.html',
  providers: [ReminderEditService, OrderDeliveryInformationService],
})
export class SingleOrderEditComponent implements OnInit {
  public pageID: number;
  public currentSection: WizardStepItem;
  public singleOrderViewModel: SingleOrderViewModel;
  public orderIsNew = false;
  public isSaving = false;
  public reminder: IReminder;
  public hasReminder: boolean;
  public wizardSteps: WizardStepItem[] = WIZARD_ITEMS;
  public form: UntypedFormGroup;
  public reminderForm: UntypedFormGroup;
  public canSetDeliveryDateUnknown: boolean;
  public warningType = InfoBoxType.Error;
  public loading = true;
  public integratedCare: IIntegratedCare;
  private config: ConfigDto;
  private _templateRef;
  private _sendDirectly: boolean;
  private _showSendOrderDialog: boolean;
  private _userId: string;

  get templateRef() {
    return this._templateRef;
  }

  set templateRef(value) {
    this._templateRef = value;
    this._changeDetectorRef.detectChanges();
  }

  get isValid(): boolean {
    const hasArticles = this.singleOrderViewModel?.orderedArticleLines?.some(
      orderedArticleLine => orderedArticleLine?.active
    );
    const formIsValid = this.form && this.form.valid && !this.isSaving && hasArticles;
    if (!this.hasReminder) {
      return formIsValid;
    }
    return formIsValid && this.reminderForm.valid;
  }

  private get mappedReminder(): IReminder {
    return {
      annotation: this.reminderForm.controls['annotation'].value,
      date: this.reminderForm.controls['date'].value,
      entityType: 'singleOrder',
      parentId: this.singleOrderViewModel.integratedCareId,
      entityId: this.singleOrderViewModel._id,
      therapy: this.singleOrderViewModel.therapyId,
      patientId: this.singleOrderViewModel.patientId,
      regionId: this.singleOrderViewModel.regionId,
      userId: this.reminder ? this.reminder.userId : this._userId,
    } as IReminder;
  }

  constructor(
    private _modalController: ModalController,
    private _navParams: NavParams,
    private _alertController: AlertController,
    private _authService: AuthService,
    private _formBuilder: UntypedFormBuilder,
    private _queryService: QueryService,
    private _singleOrderComponentModel: SingleOrderComponentModel,
    private _translateService: TranslateService,
    private _changeDetectorRef: ChangeDetectorRef,
    private _reminderEditService: ReminderEditService,
    private _tenantConfigService: TenantConfigService,
    private _orderDeliveryInformationService: OrderDeliveryInformationService
  ) {
    this.wizardSteps = WIZARD_ITEMS;
  }

  async ngOnInit(): Promise<void> {
    this._userId = await this._authService.getCurrentUserId();
    this.canSetDeliveryDateUnknown = await this._tenantConfigService.canSetDeliveryDateUnknown();
    this._showSendOrderDialog = await this._tenantConfigService.showSendOrderDialog();
    const orderTagsRequired = await this._tenantConfigService.orderTagsRequired();
    this.navigateToWizardPage();
    this.integratedCare = this._navParams.get('integratedCare');
    await this.initializeSingleOrderViewModel(
      this._navParams.get('order'),
      this.integratedCare,
      this._navParams.get('isNew')
    );
    const remindersOfOrder = await this._queryService.search<IReminder>(
      { query: `entityId:${this.singleOrderViewModel._id}` },
      ReminderDB
    );

    if (remindersOfOrder && remindersOfOrder.length > 0) {
      this.reminder = remindersOfOrder[0];
    }
    this.hasReminder = this.reminder != null;
    this.reminderForm = getReminderForm(this._formBuilder, this.reminder);
    this.form = getSingleOrderForm(this.config, this.singleOrderViewModel, this._formBuilder);
    if (this.form) {
      this.form.controls['orderedArticleLines'].markAsTouched();
      this.form.controls['orderedArticleLines'].updateValueAndValidity();
      if (orderTagsRequired) {
        this.form.controls['tags'].setValidators([Validators.required, Validators.minLength(1)]);
        this.form.controls['tags'].updateValueAndValidity();
      }
      this.form.updateValueAndValidity();
    }
    this.loading = false;
  }

  private navigateToWizardPage() {
    const pageId = this._navParams.get('pageId') as string | null;
    if (pageId) {
      this.setPage(this._navParams.get('pageId'));
    } else {
      this.setPage(0);
    }
  }

  public async changePage() {
    const sectionsModal = await this._modalController.create({
      component: WizardNavPageComponent,
      backdropDismiss: false,
      componentProps: {
        pageID: this.pageID,
        wizardSteps: this.wizardSteps,
        isEditView: true,
      },
      cssClass: 'fullPageModal',
    });
    await sectionsModal.present();
    const retVal = await sectionsModal.onDidDismiss();
    if (retVal.data !== undefined && retVal.data !== null && retVal.data >= 0) {
      this.setPage(retVal.data);
    }
  }

  public reminderToggled($event) {
    this.hasReminder = $event;
  }

  public async cancel() {
    await this._modalController.dismiss().catch(error => console.log(error));
  }

  public async done() {
    if (this._showSendOrderDialog === false || this._navParams.get('skipSave')) {
      this._sendDirectly = false;
      await this.saveOrder();
      return;
    }
    const alert = await this._alertController.create({
      header: this._translateService.instant('order.edit.sendDirectly', {
        type: 'deine Bestellung',
      }),
      backdropDismiss: false,
      buttons: [
        {
          text: this._translateService.instant('order.edit.yesSend').toUpperCase(),
          cssClass: 'primary',
          handler: async () => {
            this._sendDirectly = true;
            await this.saveOrder();
          },
        },
        {
          text: this._translateService.instant('order.edit.noJustSave').toUpperCase(),
          cssClass: 'primary',
          handler: async () => {
            this._sendDirectly = false;
            await this.saveOrder();
          },
        },
        {
          text: this._translateService.instant('CANCEL_BUTTON').toUpperCase(),
          role: 'cancel',
          cssClass: 'secondary',
        },
      ],
    });
    await alert.present();
  }

  private async saveOrder() {
    this.isSaving = true;
    this.singleOrderViewModel.clean();
    if (
      this.singleOrderViewModel.orderedArticleLines &&
      this.singleOrderViewModel.orderedArticleLines.filter(articleLine => articleLine.quantity === 0).length > 0 &&
      (await this.showZeroQuantityAlert())
    ) {
      this.isSaving = false;
      return;
    }

    let reminderViewModel;
    if (!this._navParams.get('skipSave')) {
      if (this.orderIsNew) {
        await this._singleOrderComponentModel.create(map(this.singleOrderViewModel, new SingleOrderDto()));
        reminderViewModel = await this._reminderEditService.handle(this.hasReminder, this.mappedReminder);
      } else {
        await this._singleOrderComponentModel.edit(
          this.singleOrderViewModel,
          map(this.singleOrderViewModel, new SingleOrderDto())
        );
        reminderViewModel = await this._reminderEditService.handle(
          this.hasReminder,
          this.mappedReminder,
          this.reminder
        );
      }
    }
    await this._orderDeliveryInformationService.saveDeliveryInformationToPatient(
      this.singleOrderViewModel,
      this.singleOrderViewModel.patientId
    );
    await this._modalController
      .dismiss({ order: this.singleOrderViewModel, reminder: reminderViewModel, sendDirectly: this._sendDirectly })
      .catch(error => console.log(error));
  }

  async showMaxExceededWarning(): Promise<any> {
    const alert = await this._alertController.create({
      header: this._translateService.instant('order.details.maxAmountExceeded'),
      subHeader: this._translateService.instant('validation.warning.maxAmountExceeded'),
      buttons: [this._translateService.instant('DONE_BUTTON')],
    });
    await alert.present();
  }

  public previousSection() {
    if (this.pageID > 0) {
      this.setPage(this.pageID - 1);
    }
  }

  public nextSection() {
    if (this.pageID < this.wizardSteps.length) {
      this.setPage(this.pageID + 1);
    }
  }
  private setPage(pageId: number): void {
    if (pageId != null) {
      this.pageID = pageId;
      this.currentSection = this.wizardSteps[this.pageID];
    }
  }

  private async initializeSingleOrderViewModel(
    order: ISingleOrder,
    integratedCare: IIntegratedCare,
    isNew: boolean
  ): Promise<void> {
    this.orderIsNew = isNew;
    // we already have a view model
    if (order instanceof SingleOrderViewModel) {
      this.singleOrderViewModel = order;
      return;
    }

    if (isNew) {
      this.singleOrderViewModel = new SingleOrderViewModel();
      await this.singleOrderViewModel.initializeNew(integratedCare, this._queryService, this._tenantConfigService);
      this.singleOrderViewModel.createdBy = this._userId;
    } else {
      this.singleOrderViewModel = observe(map(order, new SingleOrderViewModel()));
      await this.singleOrderViewModel.initializeExisting(
        await this._queryService.get(this.singleOrderViewModel.integratedCareId, IntegratedCareDB),
        this._queryService,
        true
      );
    }
  }

  private async showZeroQuantityAlert(): Promise<boolean> {
    return new Promise<boolean>(async resolve => {
      const alert = await this._alertController.create({
        header: this._translateService.instant('order.edit.zeroQuanityAlert.title'),
        backdropDismiss: false,
        message: this._translateService.instant('order.edit.zeroQuanityAlert.message'),
        buttons: [
          {
            text: this._translateService.instant('NO'),
            handler: () => resolve(true),
          },
          {
            text: this._translateService.instant('YES'),
            handler: () => resolve(false),
          },
        ],
      });
      await alert.present();
    });
  }
}
